import { useFetcher } from "~/hooks/useFetcher";
import {
  adminArtifactPreviewByIdPath,
  adminArtifactPreviewsPath,
  artifactPreviewByIdPath,
  artifactPreviewsPath,
  sharedArtifactPreviewByIdPath,
  sharedArtifactPreviewsPath,
  type ArtifactPreview,
} from "~/services/artifacts";

// Which endpoint family a renderer resolves artifacts through. `isAdmin` only
// applies alongside `conversationId`: the owner route refuses a cross-user
// read, so an admin reviewer needs the admin counterpart.
export interface ArtifactScope {
  conversationId: string | null;
  shareToken: string | null;
  isAdmin?: boolean;
}

// Resolves a single artifact by name against the conversations-api previews
// endpoint. Returns the first (and only) preview, narrowing the batch
// endpoint to single-name lookup.
//
// Picks the URL based on which scope the caller is in:
//   - `conversationId` set → owner endpoint, or the admin one when `isAdmin`
//   - `shareToken` set → token-scoped shares endpoint (shares-table row is
//     the authorization)
//
// Cached by SWR keyed on the request URL — repeat references to the same
// artifact reuse one in-flight request and never refetch on re-render.
export function useArtifactPreview(scope: ArtifactScope, name: string) {
  let url: string | null = null;
  if (scope.conversationId) {
    url = scope.isAdmin
      ? adminArtifactPreviewsPath(scope.conversationId, [name])
      : artifactPreviewsPath(scope.conversationId, [name]);
  } else if (scope.shareToken) {
    url = sharedArtifactPreviewsPath(scope.shareToken, [name]);
  }
  const { data, error, isLoading } = useFetcher<ArtifactPreview[]>(url);
  if (url === null) {
    return {
      preview: undefined,
      error: undefined,
      isLoading: false,
      notFound: false,
      unsupported: true,
    } as const;
  }
  const preview = data?.[0];
  // Backend returns an empty array when the name doesn't resolve — surface
  // this distinctly from a network error so the renderer can show "missing".
  const notFound = !isLoading && !error && data !== undefined && preview === undefined;
  return { preview, error, isLoading, notFound, unsupported: false } as const;
}

// Resolves a single artifact by id against the by-id preview endpoint. Unlike
// the by-name variant this returns one object (not a batch), and a missing or
// revoked artifact surfaces as `error` (404). Used for user-attachment cards,
// which key on the artifact id linked to their message.
export function useArtifactPreviewById(scope: ArtifactScope, id: string) {
  let url: string | null = null;
  if (scope.conversationId) {
    url = scope.isAdmin
      ? adminArtifactPreviewByIdPath(scope.conversationId, id)
      : artifactPreviewByIdPath(scope.conversationId, id);
  } else if (scope.shareToken) {
    url = sharedArtifactPreviewByIdPath(scope.shareToken, id);
  }
  const { data, error, isLoading } = useFetcher<ArtifactPreview>(url);
  if (url === null) {
    return {
      preview: undefined,
      error: undefined,
      isLoading: false,
      notFound: false,
      unsupported: true,
    } as const;
  }
  const notFound = !isLoading && !!error;
  return { preview: data, error, isLoading, notFound, unsupported: false } as const;
}
